/* ==========================================================================
   RasCommerce — shared utilities
   ========================================================================== */
(function () {
  'use strict';

  window.RC = window.RC || {};
  var RC = window.RC;

  RC.settings = RC.settings || {};
  RC.routes = RC.routes || {};
  RC.strings = RC.strings || {};

  /* ---------------------------------------------------------------- Money */

  RC.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || RC.settings.moneyFormat || '${{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number === null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  };

  /* --------------------------------------------------------------- Fetch */

  RC.fetchJSON = function (url, options) {
    options = options || {};
    options.headers = Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      options.headers || {}
    );
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Request failed');
          error.data = data;
          error.status = response.status;
          throw error;
        }
        return data;
      });
    });
  };

  RC.fetchText = function (url) {
    return fetch(url).then(function (response) {
      if (!response.ok) throw new Error('Request failed: ' + response.status);
      return response.text();
    });
  };

  /**
   * Fetch a rendered section and return it as a DOM element.
   * Uses the Section Rendering API.
   */
  RC.fetchSection = function (sectionId, url) {
    var target = url || window.location.pathname;
    var separator = target.indexOf('?') === -1 ? '?' : '&';
    return RC.fetchText(target + separator + 'section_id=' + sectionId).then(function (html) {
      var parser = new DOMParser();
      return parser.parseFromString(html, 'text/html');
    });
  };

  /* --------------------------------------------------------------- Toast */

  RC.toast = function (message, type, duration) {
    var stack = document.getElementById('rcToastStack');
    if (!stack) return;

    var toast = document.createElement('div');
    toast.className = 'rc-toast' + (type ? ' rc-toast-' + type : '');
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    var text = document.createElement('span');
    text.innerHTML = message;
    toast.appendChild(text);

    var close = document.createElement('button');
    close.className = 'rc-toast-close';
    close.setAttribute('aria-label', 'Close');
    close.innerHTML = '&times;';
    close.addEventListener('click', function () {
      remove();
    });
    toast.appendChild(close);

    stack.appendChild(toast);
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    var timer = setTimeout(remove, duration || 4000);

    function remove() {
      clearTimeout(timer);
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 250);
    }

    return toast;
  };

  /* ------------------------------------------------------------- Helpers */

  RC.debounce = function (fn, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait || 250);
    };
  };

  RC.storage = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        return false;
      }
    },
    remove: function (key) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        /* ignore */
      }
    }
  };

  /**
   * Register a callback that runs on load and again whenever a section is
   * re-rendered in the theme editor.
   */
  RC.onSectionLoad = function (callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        callback(document);
      });
    } else {
      callback(document);
    }

    document.addEventListener('shopify:section:load', function (event) {
      callback(event.target);
    });
  };

  /**
   * Runs ONLY when the theme editor re-renders a section — not on first load.
   * Used to re-bind behaviour that the template's main.js binds once at
   * DOM-ready, without double-binding it on the initial page load.
   */
  RC.onEditorSectionLoad = function (callback) {
    document.addEventListener('shopify:section:load', function (event) {
      callback(event.target);
    });
  };

  RC.trapFocus = function (container) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return function () {};

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    function handler(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handler);
    return function () {
      container.removeEventListener('keydown', handler);
    };
  };

  /* --------------------------------------------- Localization auto-submit */

  RC.onSectionLoad(function (scope) {
    scope.querySelectorAll('[data-rc-country-select]').forEach(function (select) {
      if (select.dataset.rcBound) return;
      select.dataset.rcBound = 'true';
      select.addEventListener('change', function () {
        var form = select.closest('form');
        if (!form) return;
        form.querySelector('[name="country_code"]').value = select.value;
        form.submit();
      });
    });

    scope.querySelectorAll('[data-rc-locale-select]').forEach(function (select) {
      if (select.dataset.rcBound) return;
      select.dataset.rcBound = 'true';
      select.addEventListener('change', function () {
        var form = select.closest('form');
        if (!form) return;
        form.querySelector('[name="locale_code"]').value = select.value;
        form.submit();
      });
    });
  });

  /* ---------------------------------------------------------------- Share */

  /* The product share popup: copy-to-clipboard plus the OS share sheet where
     the browser exposes one. Bound on document so it survives section reloads. */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      var ok = false;
      try { ok = document.execCommand("copy"); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      ok ? resolve() : reject();
    });
  }

  document.addEventListener("click", function (event) {
    var copyBtn = event.target.closest("[data-rc-copy-link]");
    if (copyBtn) {
      event.preventDefault();
      var field = document.querySelector("[data-rc-share-url]");
      var url = field ? field.value : window.location.href;
      var label = copyBtn.querySelector("span") || copyBtn;
      var original = copyBtn.dataset.rcOriginal || label.textContent;
      copyBtn.dataset.rcOriginal = original;
      copyText(url).then(function () {
        label.textContent = (RC.strings && RC.strings.linkCopied) || "Link copied";
        copyBtn.classList.add("rc-copied");
        clearTimeout(copyBtn._rcTimer);
        copyBtn._rcTimer = setTimeout(function () {
          label.textContent = original;
          copyBtn.classList.remove("rc-copied");
        }, 2000);
      }).catch(function () {
        if (field) { field.select(); }
      });
      return;
    }

    var nativeBtn = event.target.closest("[data-rc-native-share]");
    if (nativeBtn && navigator.share) {
      event.preventDefault();
      navigator.share({
        title: nativeBtn.dataset.shareTitle || document.title,
        url: nativeBtn.dataset.shareUrl || window.location.href
      }).catch(function () { /* user dismissed the sheet */ });
    }
  });

  /* Only offer the OS share sheet when the browser actually has one. */
  RC.onSectionLoad(function (scope) {
    if (!navigator.share) return;
    scope.querySelectorAll("[data-rc-native-share][hidden]").forEach(function (btn) {
      btn.hidden = false;
    });
  });
})();
